import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { AddCircle, DeleteOutline, VerifiedUser } from '@mui/icons-material';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import { procurementService, Vendor } from '../../services/procurementService';
import { projectService, Project } from '../../services/projectService';
import { SwoStatus } from './ServiceWorkOrderPage';
import { useTaxCalculation } from '../../hooks/useTaxCalculation';
import { TaxSummaryPanel } from '../../components/common/TaxSummaryPanel';
import {
  P,
  cardSx,
  labelSx,
  fieldSx,
  priBtn,
  outBtn,
  gridInput,
  thSx,
  thTitle,
  PX,
  FieldLabel,
  StatusPill,
  WorkspaceHeader,
  TotalsStrip,
  ValidationStrip,
} from '../../components/common/workspaceKit';

interface CostCenter {
  id: number;
  code: string;
  name: string;
  isActive?: boolean;
}

const toNum = (v: string | number | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

let draftKeySeq = 0;
const nextDraftKey = () => `swoline-${++draftKeySeq}`;

interface LineDraft {
  key: string;
  description: string;
  quantity: string;
  uom: string;
  rate: string;
  sacCode: string;
  costCenterId: string;
}

const emptyLine = (): LineDraft => ({
  key: nextDraftKey(),
  description: '',
  quantity: '1',
  uom: '',
  rate: '',
  sacCode: '',
  costCenterId: '',
});

interface SwoDetailResponse {
  id: number;
  vendor_id: number | string;
  vendor_name?: string;
  service_category?: string;
  plant?: string;
  department?: string;
  cost_center_id?: number | string | null;
  project_id?: number | string | null;
  wo_date?: string;
  start_date?: string | null;
  end_date?: string | null;
  scope_of_work?: string;
  payment_terms?: string;
  contract_reference?: string;
  sla?: string;
  warranty?: string;
  remarks?: string;
  tax_amount?: number | string;
  status: SwoStatus;
  items?: Array<{
    id?: number;
    description: string;
    quantity: number | string;
    uom?: string;
    rate: number | string;
    sac_code?: string;
    cost_center_id?: number | string | null;
  }>;
}

const ServiceWorkOrderFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingRefData, setLoadingRefData] = useState(true);
  const [loadingOrder, setLoadingOrder] = useState(isEdit);

  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [serviceCategory, setServiceCategory] = useState('');
  const [plant, setPlant] = useState('');
  const [department, setDepartment] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [woDate, setWoDate] = useState(todayISO());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [contractReference, setContractReference] = useState('');
  const [sla, setSla] = useState('');
  const [warranty, setWarranty] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [status, setStatus] = useState<SwoStatus | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    setLoadingRefData(true);
    Promise.all([
      procurementService.getVendors().catch(() => ({ vendors: [] as Vendor[], count: 0 })),
      // Cost centers / projects are optional context (Finance Workspace /
      // Projects module) — a tenant without that module enabled, or the
      // endpoint being unavailable, must not block raising a work order.
      api.get<{ costCenters: CostCenter[] }>('/cost-centers').catch(() => ({ costCenters: [] as CostCenter[] })),
      projectService.getProjects().catch(() => [] as Project[]),
    ])
      .then(([vendorsRes, costCentersRes, projectsRes]) => {
        setVendors((vendorsRes.vendors || []).filter((v) => v.is_active !== false));
        setCostCenters((costCentersRes.costCenters || []).filter((c) => c.isActive !== false));
        setProjects(projectsRes || []);
      })
      .finally(() => setLoadingRefData(false));
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingOrder(true);
    api
      .get<{ serviceWorkOrder: SwoDetailResponse }>(`/service-work-orders/${id}`)
      .then(({ serviceWorkOrder }) => {
        setSelectedVendor({
          id: String(serviceWorkOrder.vendor_id),
          company_name: serviceWorkOrder.vendor_name || '',
        } as Vendor);
        setServiceCategory(serviceWorkOrder.service_category || '');
        setPlant(serviceWorkOrder.plant || '');
        setDepartment(serviceWorkOrder.department || '');
        setCostCenterId(serviceWorkOrder.cost_center_id ? String(serviceWorkOrder.cost_center_id) : '');
        setProjectId(serviceWorkOrder.project_id ? String(serviceWorkOrder.project_id) : '');
        setWoDate(serviceWorkOrder.wo_date ? serviceWorkOrder.wo_date.slice(0, 10) : todayISO());
        setStartDate(serviceWorkOrder.start_date ? serviceWorkOrder.start_date.slice(0, 10) : '');
        setEndDate(serviceWorkOrder.end_date ? serviceWorkOrder.end_date.slice(0, 10) : '');
        setScopeOfWork(serviceWorkOrder.scope_of_work || '');
        setPaymentTerms(serviceWorkOrder.payment_terms || '');
        setContractReference(serviceWorkOrder.contract_reference || '');
        setSla(serviceWorkOrder.sla || '');
        setWarranty(serviceWorkOrder.warranty || '');
        setRemarks(serviceWorkOrder.remarks || '');
        setStatus(serviceWorkOrder.status);
        setLines(
          (serviceWorkOrder.items || []).map((it) => ({
            key: nextDraftKey(),
            description: it.description || '',
            quantity: String(it.quantity ?? ''),
            uom: it.uom || '',
            rate: String(it.rate ?? ''),
            sacCode: it.sac_code || '',
            costCenterId: it.cost_center_id ? String(it.cost_center_id) : '',
          }))
        );
      })
      .catch((err: any) => setError(err.message || 'Failed to load service work order'))
      .finally(() => setLoadingOrder(false));
  }, [isEdit, id]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));
  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const estimatedValue = useMemo(
    () => lines.reduce((sum, l) => sum + toNum(l.quantity) * toNum(l.rate), 0),
    [lines]
  );

  // Tax is DETERMINED, not typed.
  //
  // This form used to render a free-text "Tax Amount" box in rupees beside the
  // totals strip, so the GST on a service work order was whatever the person
  // raising it typed — no rate, no CGST/SGST/IGST split, and no way to express
  // reverse charge, which is the normal treatment for contract labour, security
  // and goods transport.
  const swoTaxLines = useMemo(
    () => lines.filter((l) => toNum(l.quantity) > 0 && toNum(l.rate) >= 0 && l.description.trim()),
    [lines]
  );

  const swoTaxInput = useMemo(() => {
    if (!selectedVendor || swoTaxLines.length === 0) return null;
    return {
      transactionType: 'SERVICE_WORK_ORDER' as const,
      partyId: Number(selectedVendor.id),
      documentDate: woDate,
      lines: swoTaxLines.map((l) => ({
        hsnCode: l.sacCode.trim() || null,
        quantity: toNum(l.quantity),
        unitPrice: toNum(l.rate),
        description: l.description.trim(),
        uom: l.uom.trim() || null,
      })),
    };
  }, [selectedVendor, woDate, swoTaxLines]);

  const { tax: swoTax, loading: swoTaxLoading, error: swoTaxError } = useTaxCalculation(swoTaxInput);

  const taxAmount = swoTax ? swoTax.totalTax : 0;
  const total = swoTax ? swoTax.totalAmount : estimatedValue;

  const canEdit = !isEdit || status === 'DRAFT';

  const validate = (): string[] => {
    const errs: string[] = [];
    if (!selectedVendor) errs.push('A vendor is required.');
    if (!serviceCategory.trim()) errs.push('Service category is required.');
    if (!woDate) errs.push('Work order date is required.');
    if (lines.length === 0) errs.push('At least one scope line is required.');
    lines.forEach((l, idx) => {
      if (!l.description.trim()) errs.push(`Line ${idx + 1}: description is required.`);
      if (toNum(l.quantity) <= 0) errs.push(`Line ${idx + 1}: quantity must be greater than 0.`);
      if (toNum(l.rate) < 0) errs.push(`Line ${idx + 1}: rate cannot be negative.`);
    });
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setValidationErrors(errs);
    if (errs.length > 0 || !selectedVendor) return;

    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        vendorId: selectedVendor.id,
        serviceCategory: serviceCategory.trim(),
        plant: plant.trim() || undefined,
        department: department.trim() || undefined,
        costCenterId: costCenterId ? Number(costCenterId) : undefined,
        projectId: projectId ? Number(projectId) : undefined,
        woDate,
        scopeOfWork: scopeOfWork.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        // No tax amount is sent — ServiceWorkOrder._computeValues determines it
        // server-side from the SAC / tax category and the vendor's state.
        paymentTerms: paymentTerms.trim() || undefined,
        contractReference: contractReference.trim() || undefined,
        sla: sla.trim() || undefined,
        warranty: warranty.trim() || undefined,
        remarks: remarks.trim() || undefined,
        items: lines.map((l) => ({
          description: l.description.trim(),
          quantity: toNum(l.quantity),
          uom: l.uom.trim() || undefined,
          rate: toNum(l.rate),
          sacCode: l.sacCode.trim() || undefined,
          costCenterId: l.costCenterId ? Number(l.costCenterId) : undefined,
        })),
      };

      const { serviceWorkOrder } =
        isEdit && id
          ? await api.put<{ serviceWorkOrder: { id: number } }>(`/service-work-orders/${id}`, payload)
          : await api.post<{ serviceWorkOrder: { id: number } }>('/service-work-orders', payload);

      navigate(`/procurement/service-work-orders/${serviceWorkOrder.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to save service work order');
    } finally {
      setSubmitting(false);
    }
  };

  const loading = loadingRefData || loadingOrder;

  if (loading) {
    return (
      <Box sx={{ bgcolor: P.bg, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const backTo = isEdit && id ? `/procurement/service-work-orders/${id}` : '/procurement/service-work-orders';
  const statusLabel = isEdit && status ? status : 'DRAFT';
  const statusTone = statusLabel === 'DRAFT' ? 'draft' : statusLabel === 'CANCELLED' ? 'red' : 'muted';

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100vh', pb: 5 }}>
      <WorkspaceHeader
        onBack={() => navigate(backTo)}
        title={isEdit ? 'Edit Service Work Order' : 'New Service Work Order'}
        subtitle="Commission an outside service before it can be entered and billed."
        chip={<StatusPill label={statusLabel} tone={statusTone as any} />}
        actions={
          canEdit && (
            <>
              <Button variant="outlined" onClick={() => navigate(backTo)} disabled={submitting} sx={outBtn}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <VerifiedUser sx={{ fontSize: 18 }} />}
                onClick={handleSubmit}
                disabled={submitting}
                sx={priBtn}
              >
                {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Work Order'}
              </Button>
            </>
          )
        }
      />

      <Box sx={{ px: PX, pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {!canEdit && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            This service work order is {status?.replace('_', ' ')} and can no longer be edited.
          </Alert>
        )}
        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {validationErrors.map((e) => (
              <Box key={e}>{e}</Box>
            ))}
          </Alert>
        )}

        {/* Metadata */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Work Order Details</Typography>
          </Box>
          <Box sx={{ p: { xs: 2, md: 2.75 }, pt: 1.75, display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' } }}>
            <Box>
              <FieldLabel text="Vendor" required />
              <Autocomplete
                size="small"
                options={vendors}
                value={selectedVendor}
                onChange={(_, v) => setSelectedVendor(v)}
                getOptionLabel={(o) => o.company_name || ''}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                disabled={!canEdit}
                renderInput={(params) => <TextField {...params} placeholder="Select vendor" sx={fieldSx} />}
              />
            </Box>
            <Box>
              <FieldLabel text="Service Category" required />
              <TextField fullWidth size="small" value={serviceCategory} onChange={(e) => setServiceCategory(e.target.value)} disabled={!canEdit} placeholder="e.g. AMC, Repairs, Consulting" sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Plant" />
              <TextField fullWidth size="small" value={plant} onChange={(e) => setPlant(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Department" />
              <TextField fullWidth size="small" value={department} onChange={(e) => setDepartment(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Cost Center" />
              <TextField select fullWidth size="small" value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)} disabled={!canEdit} sx={fieldSx}>
                <MenuItem value="">Not assigned</MenuItem>
                {costCenters.length === 0 ? (
                  <MenuItem value="" disabled>No cost centers configured</MenuItem>
                ) : (
                  costCenters.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.code} — {c.name}</MenuItem>
                  ))
                )}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="Project" />
              <TextField select fullWidth size="small" value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!canEdit} sx={fieldSx}>
                <MenuItem value="">Not assigned</MenuItem>
                {projects.length === 0 ? (
                  <MenuItem value="" disabled>No projects configured</MenuItem>
                ) : (
                  projects.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>{p.project_code} — {p.project_name}</MenuItem>
                  ))
                )}
              </TextField>
            </Box>
            <Box>
              <FieldLabel text="WO Date" required />
              <TextField type="date" fullWidth size="small" value={woDate} onChange={(e) => setWoDate(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Start Date" />
              <TextField type="date" fullWidth size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="End Date" />
              <TextField type="date" fullWidth size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Payment Terms" />
              <TextField fullWidth size="small" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} disabled={!canEdit} placeholder="e.g. Net 30" sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Contract Reference" />
              <TextField fullWidth size="small" value={contractReference} onChange={(e) => setContractReference(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="SLA" />
              <TextField fullWidth size="small" value={sla} onChange={(e) => setSla(e.target.value)} disabled={!canEdit} placeholder="e.g. 4-hour response" sx={fieldSx} />
            </Box>
            <Box>
              <FieldLabel text="Warranty" />
              <TextField fullWidth size="small" value={warranty} onChange={(e) => setWarranty(e.target.value)} disabled={!canEdit} placeholder="e.g. 12 months" sx={fieldSx} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FieldLabel text="Scope of Work" />
              <TextField fullWidth size="small" multiline minRows={2} value={scopeOfWork} onChange={(e) => setScopeOfWork(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FieldLabel text="Remarks" />
              <TextField fullWidth size="small" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canEdit} sx={fieldSx} />
            </Box>
          </Box>
        </Paper>

        {/* Scope lines — full width */}
        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
          <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${P.line}` }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: P.strong }}>Scope Lines</Typography>
            {canEdit && (
              <Button size="small" startIcon={<AddCircle sx={{ fontSize: 18 }} />} onClick={addLine} sx={{ ...outBtn, height: 34 }}>Add Line</Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}><Typography sx={thTitle}>Description <Box component="span" sx={{ color: P.red }}>*</Box></Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 100 }}><Typography sx={thTitle}>Qty</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 90 }}><Typography sx={thTitle}>UOM</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 120 }}><Typography sx={thTitle}>Rate</Typography></TableCell>
                  <TableCell sx={{ ...thSx, width: 120 }}><Typography sx={thTitle}>SAC Code</Typography></TableCell>
                  <TableCell align="right" sx={{ ...thSx, width: 130 }}><Typography sx={thTitle}>Amount</Typography></TableCell>
                  {canEdit && <TableCell sx={{ ...thSx, width: 48 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l) => (
                  <TableRow key={l.key} sx={{ '& td': { borderBottom: `1px solid ${P.tline}`, py: 0.5 } }}>
                    <TableCell>
                      <TextField size="small" fullWidth value={l.description} onChange={(e) => updateLine(l.key, { description: e.target.value })} disabled={!canEdit} placeholder="Scope description" sx={gridInput} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={l.quantity} onChange={(e) => updateLine(l.key, { quantity: e.target.value })} inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={l.uom} onChange={(e) => updateLine(l.key, { uom: e.target.value })} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={l.rate} onChange={(e) => updateLine(l.key, { rate: e.target.value })} inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={l.sacCode} onChange={(e) => updateLine(l.key, { sacCode: e.target.value })} disabled={!canEdit} fullWidth sx={gridInput} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: P.strong, whiteSpace: 'nowrap', fontSize: '13.5px' }}>
                      {formatIndianNumber(toNum(l.quantity) * toNum(l.rate))}
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => removeLine(l.key)} disabled={lines.length === 1} sx={{ color: P.muted, '&:hover': { color: P.red } }}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Totals + the server-determined tax breakup. The free-text "Tax
            Amount" box that used to sit here is gone: GST on a service is
            resolved from the SAC / tax category and the vendor's state. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 380px' }, gap: 1.5, mb: 1.5 }}>
          <TotalsStrip
            cells={[
              { label: 'Line Items', value: lines.length },
              { label: 'Taxable', value: `${formatIndianNumber(swoTax ? swoTax.subtotal : estimatedValue)}` },
              { label: 'Tax', value: `${formatIndianNumber(taxAmount)}` },
              { label: 'Total', value: `${formatIndianNumber(total)}`, tone: 'primary' },
              { label: 'Status', value: statusLabel },
            ]}
          />
          <TaxSummaryPanel tax={swoTax} loading={swoTaxLoading} error={swoTaxError} />
        </Box>

        {/* Validation strip */}
        <ValidationStrip
          items={[
            { ok: !!selectedVendor, label: 'Vendor selected' },
            { ok: !!woDate, label: 'WO date set' },
            { ok: lines.length > 0, label: 'At least one line' },
          ]}
        />
      </Box>
    </Box>
  );
};

export default ServiceWorkOrderFormPage;
